import { classMap } from 'lit/directives/class-map.js';
import { html } from 'lit';
import { monitor } from '../../common/monitor.js';
import { property, query, state } from 'lit/decorators.js';
import { scrollIntoView } from '../../common/scroll.js';
import componentStyles from '../../styles/component.styles.js';
import styles from './tab-group.styles.js';
import WebModuleElement from '../../common/webmodule-element.js';
import type { CSSResultGroup } from 'lit';
import type WebmoduleTab from '../tab/tab.js';
import type WebmoduleTabPanel from '../tab-panel/tab-panel.js';

/**
 * @slot - Used for grouping tab panels in the tab group. Must be `<webmodule-tab-panel>` elements.
 * @slot nav - Used for grouping tabs in the tab group. Must be `<webmodule-tab>` elements.
 *
 * @event {{ name: String }} webmodule-tab-before-show - Emitted when a tab is requested via user interaction (click or
 * keyboard), before the tab group switches to it. Calling `preventDefault()` cancels the tab change, leaving the
 * current tab active. Not emitted for programmatic changes via `show()`.
 * @event {{ name: String }} webmodule-tab-show - Emitted when a tab is shown.
 * @event {{ name: String }} webmodule-tab-hide - Emitted when a tab is hidden.
 *
 * @csspart base - The component's base wrapper.
 * @csspart nav - The tab group's navigation container where tabs are slotted in.
 * @csspart tabs - The container that wraps the tabs.
 * @csspart active-tab-indicator - The line that highlights the currently selected tab.
 * @csspart body - The tab group's body where tab panels are slotted in.
 * @csspart scroll-button - The previous/next scroll buttons that show when tabs are scrollable, an `<webmodule-icon-button>`.
 * @csspart scroll-button--start - The starting scroll button.
 * @csspart scroll-button--end - The ending scroll button.
 * @csspart scroll-button__base - The scroll button's exported `base` part.
 *
 * @cssproperty --indicator-color - The color of the pill that glides behind the active tab.
 * @cssproperty --rail-padding - Optional inset around the tab strip (also offsets the pill). 0 by default.
 *
 * @tag webmodule-tab-group
 */
export default class WebmoduleTabGroup extends WebModuleElement {
  static styles: CSSResultGroup = [componentStyles, styles];

  @query('.tab-group') tabGroup: HTMLElement;
  @query('.tab-group__body') body: HTMLSlotElement;
  @query('.tab-group__nav') nav: HTMLElement;
  @query('.tab-group__indicator') indicator: HTMLElement;
  @property() placement: 'top' | 'bottom' | 'start' | 'end' = 'top';
  /**
   * Visual style. 'classic' renders filled folder tabs whose active tab lifts
   * to the surface colour (no pill); 'chip' renders outlined chips with a
   * solid pill gliding behind the active tab. Stamped onto the slotted tabs
   * as data-variant so their shadow styles can branch on it.
   */
  @property({ reflect: true }) variant: 'classic' | 'chip' = 'classic';
  @property() activation: 'auto' | 'manual' = 'auto';
  @property({ attribute: 'no-scroll-controls', type: Boolean }) noScrollControls = false;
  private activeTab?: WebmoduleTab;
  private mutationObserver: MutationObserver;
  private resizeObserver: ResizeObserver;
  private tabs: WebmoduleTab[] = [];
  private panels: WebmoduleTabPanel[] = [];
  @state() private hasScrollControls = false;

  connectedCallback() {
    const whenAllDefined = Promise.all([
      customElements.whenDefined('webmodule-tab'),
      customElements.whenDefined('webmodule-tab-panel')
    ]);

    super.connectedCallback();

    this.resizeObserver = new ResizeObserver(() => {
      this.repositionIndicator();
      this.updateScrollControls();
    });

    this.mutationObserver = new MutationObserver(mutations => {
      // Sync tabs when disabled states change
      if (mutations.some(m => m.attributeName === 'disabled')) {
        this.syncTabsAndPanels();
      }
    });

    // After the first update...
    this.updateComplete.then(() => {
      this.syncTabsAndPanels();
      this.mutationObserver.observe(this, { attributes: true, childList: true, subtree: true });
      this.resizeObserver.observe(this.nav);

      // Wait for tabs and tab panels to be registered
      whenAllDefined.then(() => {
        // Set initial tab state when the tabs become visible
        const intersectionObserver = new IntersectionObserver((entries, observer) => {
          if (entries[0].intersectionRatio > 0) {
            this.setActiveTab(this.getActiveTab() ?? this.tabs[0], { emitEvents: false });
            observer.unobserve(entries[0].target);
          }
        });
        intersectionObserver.observe(this.tabGroup);
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.mutationObserver?.disconnect();
    this.resizeObserver?.unobserve(this.nav);
  }

  @monitor('noScrollControls', { delayMonitorUntilFirstUpdate: true })
  updateScrollControls() {
    if (this.noScrollControls) {
      this.hasScrollControls = false;
    } else {
      // Safari appears to calculate this incorrectly when zoomed at 110%, causing the controls to toggle indefinitely.
      // Adding a single pixel to the comparison seems to resolve it.
      this.hasScrollControls =
        ['top', 'bottom'].includes(this.placement) && this.nav.scrollWidth > this.nav.clientWidth + 1;
    }
  }

  @monitor('placement', { delayMonitorUntilFirstUpdate: true })
  syncIndicator() {
    const tab = this.getActiveTab();

    // classic mode has no pill; the active tab's surface fill is the state
    if (tab && this.variant === 'chip') {
      this.indicator.style.display = 'block';
      this.repositionIndicator();
    } else {
      this.indicator.style.display = 'none';
    }
  }

  @monitor('variant', { delayMonitorUntilFirstUpdate: true })
  handleVariantChange() {
    this.stampTabVariant();
    this.syncIndicator();
  }

  /**
   * Push the group's variant onto the slotted tabs as `data-variant`.
   *
   * Why stamping at all: a tab's shadow styles cannot see an attribute on the
   * group, so the variant must be mirrored onto each tab host for
   * `:host([data-variant='chip'])` rules to apply.
   *
   * Why the GROUP stamps (and not a consumer such as wm-tab-manager, which
   * also holds references to every tab): the tab family is used without the
   * TabManager too - raw markup pages and ComponentApp compose
   * webmodule-tab-group directly. If a consumer were responsible for
   * stamping, `variant` would silently do nothing everywhere that consumer
   * is not involved, and a forgotten stamp would produce a mixed strip. The
   * group is the lowest common ancestor every usage shares, so stamping here
   * keeps the attribute self-contained (set `variant` on the group, the
   * whole family follows) and makes it impossible for tabs to disagree with
   * their group. It matches the coordination the group already owns:
   * roles, active-state sync, and the ::slotted custom properties.
   * Consumers own only their parts outside the group (e.g. a button bar).
   */
  private stampTabVariant() {
    this.getAllTabs().forEach(tab => tab.setAttribute('data-variant', this.variant));
  }

  show(panel: string) {
    const tab = this.tabs.find(el => el.panel === panel);

    if (tab) {
      this.setActiveTab(tab, { scrollBehavior: 'smooth' });
    }
  }

  render() {
    return html`
      <div
        part="base"
        class=${classMap({
          'tab-group': true,
          'tab-group--top': this.placement === 'top',
          'tab-group--bottom': this.placement === 'bottom',
          'tab-group--start': this.placement === 'start',
          'tab-group--end': this.placement === 'end',
          'tab-group--has-scroll-controls': this.hasScrollControls
        })}
        @click=${this.handleClick}
        @keydown=${this.handleKeyDown}
      >
        <div class="tab-group__nav-container" part="nav">
          ${this.hasScrollControls
            ? html`
                <webmodule-icon-button
                  part="scroll-button scroll-button--start"
                  exportparts="base:scroll-button__base"
                  class="tab-group__scroll-button tab-group__scroll-button--start"
                  name="chevron-left"
                  library="default"
                  label="Scroll To Start"
                  @click=${this.handleScrollToStart}
                ></webmodule-icon-button>
              `
            : ''}

          <div class="tab-group__nav">
            <div part="tabs" class="tab-group__tabs" role="tablist">
              <div part="active-tab-indicator" class="tab-group__indicator"></div>
              <slot name="nav" @slotchange=${this.syncTabsAndPanels}></slot>
            </div>
          </div>

          ${this.hasScrollControls
            ? html`
                <webmodule-icon-button
                  part="scroll-button scroll-button--end"
                  exportparts="base:scroll-button__base"
                  class="tab-group__scroll-button tab-group__scroll-button--end"
                  name="chevron-right"
                  library="default"
                  label="Scroll To End"
                  @click=${this.handleScrollToEnd}
                ></webmodule-icon-button>
              `
            : ''}
        </div>

        <slot part="body" class="tab-group__body" @slotchange=${this.syncTabsAndPanels}></slot>
      </div>
    `;
  }

  private getAllTabs(options: { includeDisabled: boolean } = { includeDisabled: true }) {
    const slot = this.shadowRoot!.querySelector<HTMLSlotElement>('slot[name="nav"]')!;

    return [...(slot.assignedElements() as WebmoduleTab[])].filter(el => {
      return options.includeDisabled
        ? el.tagName.toLowerCase() === 'webmodule-tab'
        : el.tagName.toLowerCase() === 'webmodule-tab' && !el.disabled;
    });
  }

  private getAllPanels() {
    return [...this.body.assignedElements()].filter(el => el.tagName.toLowerCase() === 'webmodule-tab-panel') as [
      WebmoduleTabPanel
    ];
  }

  private getActiveTab() {
    return this.tabs.find(el => el.active);
  }

  private handleClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const tab = target.closest('webmodule-tab');
    const tabGroup = tab?.closest('webmodule-tab-group');

    // Ensure the target tab is in this tab group
    if (tabGroup !== this) {
      return;
    }

    if (tab !== null) {
      this.requestSetActiveTab(tab, { scrollBehavior: 'smooth' });
    }
  }

  private handleKeyDown(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    const tab = target.closest('webmodule-tab');
    const tabGroup = tab?.closest('webmodule-tab-group');

    // Ensure the target tab is in this tab group
    if (tabGroup !== this) {
      return;
    }

    // Activate a tab
    if (['Enter', ' '].includes(event.key)) {
      if (tab !== null) {
        this.requestSetActiveTab(tab, { scrollBehavior: 'smooth' });
        event.preventDefault();
      }
    }

    // Move focus left or right
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      const activeEl = this.tabs.find(t => t.matches(':focus'));

      if (activeEl?.tagName.toLowerCase() === 'webmodule-tab') {
        let index = this.tabs.indexOf(activeEl);

        if (event.key === 'Home') {
          index = 0;
        } else if (event.key === 'End') {
          index = this.tabs.length - 1;
        } else if (
          (['top', 'bottom'].includes(this.placement) && event.key === 'ArrowLeft') ||
          (['start', 'end'].includes(this.placement) && event.key === 'ArrowUp')
        ) {
          index--;
        } else if (
          (['top', 'bottom'].includes(this.placement) && event.key === 'ArrowRight') ||
          (['start', 'end'].includes(this.placement) && event.key === 'ArrowDown')
        ) {
          index++;
        }

        if (index < 0) {
          index = this.tabs.length - 1;
        }

        if (index > this.tabs.length - 1) {
          index = 0;
        }

        const currentTab = this.tabs[index];
        currentTab.tabIndex = 0;
        currentTab.focus({ preventScroll: true });

        if (this.activation === 'auto') {
          this.requestSetActiveTab(currentTab, { scrollBehavior: 'smooth' });
        } else {
          this.tabs.forEach(tabEl => {
            tabEl.tabIndex = tabEl === currentTab ? 0 : -1;
          });
        }

        if (['top', 'bottom'].includes(this.placement)) {
          scrollIntoView(this.tabs[index], this.nav, 'horizontal');
        }

        event.preventDefault();
      }
    }
  }

  private handleScrollToStart() {
    this.nav.scroll({
      left: this.nav.scrollLeft - this.nav.clientWidth,
      behavior: 'smooth'
    });
  }

  private handleScrollToEnd() {
    this.nav.scroll({
      left: this.nav.scrollLeft + this.nav.clientWidth,
      behavior: 'smooth'
    });
  }

  // Interactive activation (click/keyboard) goes through here so consumers can veto the change;
  // programmatic paths (show(), initial sync) call setActiveTab directly and emit no before event.
  private requestSetActiveTab(tab: WebmoduleTab, options?: { scrollBehavior?: 'auto' | 'smooth' }) {
    if (tab === this.activeTab || tab.disabled) {
      return;
    }

    const beforeShowEvent = this.emit('webmodule-tab-before-show', {
      detail: { name: tab.panel },
      cancelable: true
    });

    if (!beforeShowEvent.defaultPrevented) {
      this.setActiveTab(tab, options);
    }
  }

  private setActiveTab(tab: WebmoduleTab, options?: { emitEvents?: boolean; scrollBehavior?: 'auto' | 'smooth' }) {
    options = {
      emitEvents: true,
      scrollBehavior: 'auto',
      ...options
    };

    if (tab !== this.activeTab && !tab.disabled) {
      const previousTab = this.activeTab;
      this.activeTab = tab;

      // Sync active tab and panel
      this.tabs.forEach(el => {
        el.active = el === this.activeTab;
        el.tabIndex = el === this.activeTab ? 0 : -1;
      });
      this.panels.forEach(el => (el.active = el.name === this.activeTab?.panel));
      this.syncIndicator();

      if (['top', 'bottom'].includes(this.placement)) {
        scrollIntoView(this.activeTab, this.nav, 'horizontal', options.scrollBehavior);
      }

      // Emit events
      if (options.emitEvents) {
        if (previousTab) {
          this.emit('webmodule-tab-hide', { detail: { name: previousTab.panel } });
        }

        this.emit('webmodule-tab-show', { detail: { name: this.activeTab.panel } });
      }
    }
  }

  private repositionIndicator() {
    const currentTab = this.getActiveTab();

    if (!currentTab) {
      return;
    }

    const width = currentTab.clientWidth;
    const height = currentTab.clientHeight;

    // We can't used offsetLeft/offsetTop here due to a shadow parent issue where neither can getBoundingClientRect
    // because it provides invalid values for animating elements: https://bugs.chromium.org/p/chromium/issues/detail?id=920069
    const allTabs = this.getAllTabs();
    const precedingTabs = allTabs.slice(0, allTabs.indexOf(currentTab));
    // the flex gap between tabs ("normal" parses to NaN -> 0)
    const tabsStyle = getComputedStyle(this.indicator.parentElement!);
    const offset = precedingTabs.reduce(
      (previous, current) => ({
        left: previous.left + current.clientWidth,
        top: previous.top + current.clientHeight
      }),
      { left: 0, top: 0 }
    );

    switch (this.placement) {
      case 'top':
      case 'bottom': {
        const gap = parseFloat(tabsStyle.columnGap) || 0;
        // cross-axis thickness comes from the stylesheet (top/bottom: var(--rail-padding))
        this.indicator.style.width = `${width}px`;
        this.indicator.style.height = '';
        this.indicator.style.translate = `${offset.left + precedingTabs.length * gap}px`;
        break;
      }

      case 'start':
      case 'end': {
        const gap = parseFloat(tabsStyle.rowGap) || 0;
        this.indicator.style.width = '';
        this.indicator.style.height = `${height}px`;
        this.indicator.style.translate = `0 ${offset.top + precedingTabs.length * gap}px`;
        break;
      }
    }
  }

  // This stores tabs and panels in a cache instead of calling querySelectorAll() multiple times.
  private syncTabsAndPanels() {
    this.tabs = this.getAllTabs({ includeDisabled: false });

    this.panels = this.getAllPanels();
    this.stampTabVariant();
    this.syncIndicator();

    // After updating, show or hide scroll controls as needed
    this.updateComplete.then(() => this.updateScrollControls());
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'webmodule-tab-group': WebmoduleTabGroup;
  }
}
